declare global {
  interface Window {
    OptanonActiveGroups: string // Comma-separated list of values in OneTrustCookieCategory
    OptanonWrapper?: () => void // Function called when OneTrust consent is updated
    OneTrust: {
      ToggleInfoDisplay: () => void
    }
    gtag?: any // Loaded by Google Tag Manager if/when performance consent is granted
  }
}

export const enum OneTrustCookieCategory {
  StrictlyNecessary = 'C0001',
  Performance = 'C0002',
  Functional = 'C0003',
  Targeting = 'C0004'
}

export const ONETRUST_CONSENT_UPDATE_EVENT = 'oneTrustConsentUpdate'

/**
 * Initial polling for OneTrust to be ready and calls the provided
 * callback when ready. Returns a cleanup function to stop polling.
 */
function initialPollForOneTrust({
  callback
}: {
  callback: (consent: string) => void
}): () => void {
  if (typeof window === 'undefined') {
    return () => {}
  }

  const maxAttempts = 100
  let attempts = 0

  const interval = setInterval(() => {
    attempts++

    if (window.OptanonActiveGroups !== undefined) {
      callback(window.OptanonActiveGroups)
      clearInterval(interval)
    } else if (attempts >= maxAttempts) {
      console.warn('Stopped polling for OneTrust after max attempts')
      callback('')
      clearInterval(interval)
    }
  }, 100) // Poll every 100ms

  return () => clearInterval(interval)
}

/**
 * Sets up a listener for OneTrust consent updates.
 * Returns a cleanup function to remove the listener.
 */
function listenForOneTrustUpdates(
  onUpdate: (consent: string) => void
): () => void {
  if (typeof window === 'undefined') {
    return () => {}
  }

  const handleConsentUpdate = (event: CustomEvent) => {
    onUpdate(event.detail.consent)
  }

  window.addEventListener(
    ONETRUST_CONSENT_UPDATE_EVENT,
    handleConsentUpdate as EventListener
  )

  return () => {
    window.removeEventListener(
      ONETRUST_CONSENT_UPDATE_EVENT,
      handleConsentUpdate as EventListener
    )
  }
}

/**
 * Gets OneTrust consent for performance cookies and subscribes to updates.
 * Calls the callback with a boolean indicating if performance consent is granted.
 * Returns a cleanup function to remove all listeners.
 */
export function getOneTrustConsent(
  callback: (hasPerformanceConsent: boolean) => void
): () => void {
  if (typeof window === 'undefined') {
    return () => {}
  }

  const checkConsent = (consentedCategories: string) => {
    const hasPerformanceConsent = consentedCategories.includes(
      OneTrustCookieCategory.Performance
    )
    callback(hasPerformanceConsent)
  }

  // If consent is already available, call callback immediately
  if (window.OptanonActiveGroups !== undefined) {
    checkConsent(window.OptanonActiveGroups)
  }

  // Set up polling for initial consent
  const cleanupPolling = initialPollForOneTrust({
    callback: checkConsent
  })

  // Set up listener for consent updates
  const cleanupListener = listenForOneTrustUpdates(checkConsent)

  // Return cleanup function that removes both polling and listener
  return () => {
    cleanupPolling()
    cleanupListener()
  }
}
